import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToFirstScrollableAncestor} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Toggle} from '@dropbox/dig-components/controls';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  ChevronDownLine,
  ChevronRightLine,
  DragHandleLine,
  SearchLine,
} from '@dropbox/dig-icons/assets';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import {
  EditordocService,
  Workstream,
  WorkstreamBasic,
  WorkstreamDependencyEdit,
  WorkstreamDependencyType,
  WorkstreamEdit,
  type WorkstreamPillarLevelConfigAdminResponse,
  WorkstreamPrivacy,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {StatusChip} from 'components/DSYS/StatusChip';
import {Title} from 'components/DSYS/Title';
import {OwnerLink} from 'components/workstreams/OwnerLink';
import {ROUTE_PATHS} from 'constant';
import {canManagePillarAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue, useSetAtom} from 'jotai';
import type {MouseEvent} from 'react';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router';
import {getService} from 'utilities';
import {createPlanningDocUrl} from 'views/planning/utils';
import {useTimeframes} from 'views/workstreams/detail/hooks';
import {
  parseWorkstreamTimeframe,
  type WorkstreamTimeframe,
} from 'views/workstreams/detail/workstreamTimeframe';
import {
  useTrackerOptions,
  useWorkstream,
  useWorkstreamCreate,
  useWorkstreamEdit,
} from 'views/workstreams/hooks';
import {getWorkstreamDri} from 'views/workstreams/utils';
import {
  WorkstreamTypeahead,
  WorkstreamTypeaheadComponent,
} from 'views/workstreams/WorkstreamTypeahead';

import styles from './AdminPillars.module.css';
import {NarrativeUrlsPanel} from './NarrativeUrlsPanel';
import {useAdminNarrativeRoot} from './useAdminNarrativeRoot';
import {
  useAdminPillarLevelConfigs,
  useUpdateAdminPillarLevelConfig,
} from './useAdminPillarLevelConfigs';

type WorkstreamDependencyRecord = Workstream['dependencies'][number];
type DependentWorkstream = WorkstreamDependencyRecord['dependent'];

const isTracker = (workstream: Pick<WorkstreamBasic, 'status_cadence'>) =>
  workstream.status_cadence === 'none';

const getNarrativeStatus = (workstream: Workstream['dependencies'][number]['dependent']) =>
  workstream.updates?.[0]?.status ?? null;

const buildDependencyEdits = (
  dependencyIds: number[],
  existingDependencies: Workstream['dependencies']
): WorkstreamDependencyEdit[] =>
  dependencyIds.map((dependentId, index) => ({
    dependent_id: dependentId,
    type:
      existingDependencies.find((dependency) => dependency.dependent.id === dependentId)?.type ??
      WorkstreamDependencyType.DEPENDS_ON,
    order: index + 1,
  }));

const sortDependencies = (dependencies: Workstream['dependencies']): WorkstreamDependencyRecord[] =>
  [...dependencies].sort((a, b) => {
    const orderDifference = (a.order ?? 0) - (b.order ?? 0);
    if (orderDifference !== 0) {
      return orderDifference;
    }

    return a.dependent.name.localeCompare(b.dependent.name);
  });

const getSortedDependencyIds = (dependencies: Workstream['dependencies']) =>
  sortDependencies(dependencies).map((dependency) => dependency.dependent.id);

const reorderVisibleDependencyIds = ({
  allDependencies,
  visibleDependencyIds,
  reorderedVisibleIds,
}: {
  allDependencies: Workstream['dependencies'];
  visibleDependencyIds: number[];
  reorderedVisibleIds: number[];
}) => {
  const reorderedQueue = [...reorderedVisibleIds];
  const visibleIds = new Set(visibleDependencyIds);

  return getSortedDependencyIds(allDependencies).map((dependencyId) =>
    visibleIds.has(dependencyId) ? (reorderedQueue.shift() ?? dependencyId) : dependencyId
  );
};

const useReorderSensors = () =>
  useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

const getSortableStyle = ({
  isDragging,
  isSorting,
  transform,
  transition,
}: {
  isDragging: boolean;
  isSorting: boolean;
  transform: {x: number; y: number} | null;
  transition?: string;
}) => ({
  transition: transition ?? undefined,
  transform: `translate3d(${transform?.x ?? 0}px, ${transform?.y ?? 0}px, 0)`,
  zIndex: isDragging ? 1 : undefined,
  opacity: isSorting && !isDragging ? 0.7 : 1,
});

const toWorkstreamEdit = (
  workstream: Workstream,
  dependencies: WorkstreamDependencyEdit[] = buildDependencyEdits(
    workstream.dependencies.map((dependency) => dependency.dependent.id),
    workstream.dependencies
  )
): WorkstreamEdit => ({
  id: workstream.id,
  name: workstream.name,
  start_date: workstream.start_date,
  end_date: workstream.end_date,
  status_due: workstream.status_due,
  status_cadence: workstream.status_cadence,
  pillar: workstream.pillar ?? undefined,
  status_due_date: workstream.status_due_date,
  status_update_needed: workstream.status_update_needed,
  employee_associations: workstream.employee_associations.map(({employee, dri}) => ({
    user_id: employee.user_id,
    dri,
  })),
  links: workstream.links.map(({id, icon, text, url}) => ({
    id,
    icon,
    text,
    url,
  })),
  privacy: workstream.privacy,
  description: workstream.description,
  private_employees: workstream.private_employees?.map(({user_id}) => user_id),
  private_teams: workstream.private_teams?.map(({team_id}) => team_id),
  dependencies,
  action_type: workstream.action_type,
});

const NarrativeOwner = ({
  owner,
}: {
  owner: ReturnType<typeof getWorkstreamDri> | null | undefined;
}) => (owner ? <OwnerLink owner={owner} /> : <Text color="subtle">Unassigned</Text>);

const NarrativeStatus = ({status}: {status: string | null}) =>
  status ? <StatusChip status={status} /> : <Text color="subtle">No status</Text>;

const ListHeader = () => (
  <Box className={styles.listHeader}>
    <Box />
    <Text size="small" color="subtle">
      Workstream
    </Text>
    <Text size="small" color="subtle">
      DRI
    </Text>
    <Text size="small" color="subtle">
      Status
    </Text>
    <Text size="small" color="subtle">
      Action
    </Text>
  </Box>
);

const SortableHandle = ({
  label,
  disabled,
  isDragging,
  attributes,
  listeners,
}: {
  label: string;
  disabled?: boolean;
  isDragging: boolean;
  attributes: any;
  listeners: any;
}) => (
  <button
    type="button"
    aria-label={label}
    className={styles.dragHandle}
    disabled={disabled}
    onClick={(event) => {
      event.stopPropagation();
    }}
    {...attributes}
    {...listeners}
  >
    <UIIcon
      src={DragHandleLine}
      className={atoms({
        color: disabled ? 'Disabled On Surface' : isDragging ? 'Border Base' : 'Border Subtle',
      })}
    />
  </button>
);

type ExpandedPillarPanelProps = {
  pillarId: number;
  pillarName: string;
  narrativeRootId: number;
};

const SortablePillarRow = ({
  pillar,
  isExpanded,
  disabled,
  includePillarAsLevelOne,
  isLevelConfigLoading,
  isUpdatingLevelConfig,
  onToggle,
  onLevelConfigChange,
  onRemove,
}: {
  pillar: WorkstreamBasic;
  isExpanded: boolean;
  disabled: boolean;
  includePillarAsLevelOne: boolean | undefined;
  isLevelConfigLoading: boolean;
  isUpdatingLevelConfig: boolean;
  onToggle: () => void;
  onLevelConfigChange: (includePillarAsLevelOne: boolean) => void;
  onRemove: () => void;
}) => {
  const {attributes, listeners, isDragging, isSorting, setNodeRef, transform, transition} =
    useSortable({
      id: pillar.id,
      disabled,
    });

  return (
    <Box
      ref={setNodeRef}
      className={styles.pillarRow}
      style={getSortableStyle({
        isDragging,
        isSorting,
        transform,
        transition,
      })}
      onClick={onToggle}
    >
      <SortableHandle
        label={`Reorder ${pillar.name}`}
        disabled={disabled}
        isDragging={isDragging}
        attributes={attributes}
        listeners={listeners}
      />
      <Box display="flex" alignItems="center" style={{gap: 8, minWidth: 0}}>
        <UIIcon
          src={isExpanded ? ChevronDownLine : ChevronRightLine}
          className={atoms({color: 'Text Subtle'})}
        />
        <Text isBold>{pillar.name}</Text>
      </Box>
      <Text color="subtle">{pillar.dependencies_count ?? 0}</Text>
      <Box
        onClick={(event: MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
        }}
      >
        {isLevelConfigLoading ? (
          <Spinner size="small" />
        ) : (
          <Toggle
            aria-label={`Include ${pillar.name} as level 1`}
            checked={includePillarAsLevelOne ?? false}
            disabled={isUpdatingLevelConfig || includePillarAsLevelOne === undefined}
            onChange={() => {
              onLevelConfigChange(!(includePillarAsLevelOne ?? false));
            }}
          />
        )}
      </Box>
      <Box>
        <Button
          variant="opacity"
          size="small"
          disabled={disabled}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          Remove Pillar
        </Button>
      </Box>
    </Box>
  );
};

const SortableNarrativeRow = ({
  narrative,
  onRemove,
  dragDisabled,
  removeDisabled,
  isRemoving,
}: {
  narrative: DependentWorkstream;
  onRemove: (workstreamId: number) => void;
  dragDisabled: boolean;
  removeDisabled: boolean;
  isRemoving: boolean;
}) => {
  const {attributes, listeners, isDragging, isSorting, setNodeRef, transform, transition} =
    useSortable({
      id: narrative.id,
      disabled: dragDisabled,
    });

  return (
    <Box
      ref={setNodeRef}
      className={styles.listRow}
      style={getSortableStyle({
        isDragging,
        isSorting,
        transform,
        transition,
      })}
    >
      <SortableHandle
        label={`Reorder ${narrative.name}`}
        disabled={dragDisabled}
        isDragging={isDragging}
        attributes={attributes}
        listeners={listeners}
      />
      <Box className={styles.ellipsisCell}>
        <Link
          to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', narrative.id.toString())}
          hasNoUnderline
          showUnderlineOnHover
        >
          <Text>{narrative.name}</Text>
        </Link>
      </Box>
      <NarrativeOwner owner={getWorkstreamDri(narrative)} />
      <NarrativeStatus status={getNarrativeStatus(narrative)} />
      <Button
        className={styles.actionButton}
        variant="opacity"
        size="small"
        isLoading={isRemoving}
        disabled={removeDisabled && !isRemoving}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          onRemove(narrative.id);
        }}
      >
        Remove from Pillar
      </Button>
    </Box>
  );
};

const ExpandedPillarPanel = ({pillarId, pillarName, narrativeRootId}: ExpandedPillarPanelProps) => {
  const setSnackbar = useSetAtom(snackbarAtom);
  const {workstream: pillar, isLoading} = useWorkstream(pillarId);
  const {editWorkstream, isPending} = useWorkstreamEdit();
  const sensors = useReorderSensors();
  const [attachPendingId, setAttachPendingId] = useState<number | null>(null);
  const [detachPendingId, setDetachPendingId] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  const currentNarratives = useMemo(
    () =>
      sortDependencies(pillar?.dependencies ?? []).filter(
        ({dependent}) => !dependent.archived && !isTracker(dependent)
      ),
    [pillar]
  );

  const [orderedNarratives, setOrderedNarratives] = useState<DependentWorkstream[] | null>(null);

  useEffect(() => {
    setOrderedNarratives(currentNarratives.map(({dependent}) => dependent));
  }, [currentNarratives]);

  const displayedNarratives =
    orderedNarratives ?? currentNarratives.map(({dependent}) => dependent);

  const attachedIds = useMemo(
    () => new Set(displayedNarratives.map(({id}) => id)),
    [displayedNarratives]
  );

  const narrativeIds = useMemo(() => displayedNarratives.map(({id}) => id), [displayedNarratives]);
  const isAttaching = attachPendingId !== null;
  const excludedNarrativeTypeaheadIds = useMemo(
    () => [...attachedIds, pillarId, narrativeRootId],
    [attachedIds, narrativeRootId, pillarId]
  );

  const updatePillarDependencies = async (dependencyIds: number[]) => {
    if (!pillar) {
      return;
    }

    await editWorkstream({
      data: toWorkstreamEdit(pillar, buildDependencyEdits(dependencyIds, pillar.dependencies)),
    });
  };

  const handleNarrativeDragEnd = async ({active, over}: DragEndEvent) => {
    if (!pillar || !over || active.id === over.id || isPending || isReordering) {
      return;
    }

    const oldIndex = displayedNarratives.findIndex(({id}) => id === active.id);
    const newIndex = displayedNarratives.findIndex(({id}) => id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const previousNarratives = displayedNarratives;
    const nextNarratives = arrayMove(displayedNarratives, oldIndex, newIndex);

    setOrderedNarratives(nextNarratives);
    setIsReordering(true);

    try {
      await updatePillarDependencies(
        reorderVisibleDependencyIds({
          allDependencies: pillar.dependencies,
          visibleDependencyIds: previousNarratives.map(({id}) => id),
          reorderedVisibleIds: nextNarratives.map(({id}) => id),
        })
      );
    } catch {
      setOrderedNarratives(previousNarratives);
    } finally {
      setIsReordering(false);
    }
  };

  const handleAttach = async (workstream: WorkstreamBasic) => {
    if (!pillar) {
      return;
    }

    const nextDependencyIds = [...getSortedDependencyIds(pillar.dependencies), workstream.id];

    setAttachPendingId(workstream.id);
    try {
      await updatePillarDependencies(nextDependencyIds);
      setOrderedNarratives((current) => [
        ...(current ?? displayedNarratives),
        workstream as DependentWorkstream,
      ]);
      setSnackbar({text: `${workstream.name} attached.`});
    } finally {
      setAttachPendingId(null);
    }
  };

  const handleDetach = async (workstreamId: number) => {
    if (!pillar) {
      return;
    }

    const workstream = currentNarratives.find(
      (item) => item.dependent.id === workstreamId
    )?.dependent;
    const nextDependencyIds = getSortedDependencyIds(pillar.dependencies).filter(
      (dependencyId) => dependencyId !== workstreamId
    );

    setDetachPendingId(workstreamId);
    try {
      await updatePillarDependencies(nextDependencyIds);
      setOrderedNarratives((current) =>
        (current ?? displayedNarratives).filter(({id}) => id !== workstreamId)
      );
      setSnackbar({text: `${workstream?.name ?? 'Workstream'} removed.`});
    } finally {
      setDetachPendingId(null);
    }
  };

  if (isLoading) {
    return (
      <Box className={styles.expandedPanel} padding="16">
        <Box display="flex" justifyContent="center" paddingY="16">
          <Spinner size="small" />
        </Box>
      </Box>
    );
  }

  if (!pillar) {
    return (
      <Box className={styles.expandedPanel} padding="16">
        <Text color="subtle">Failed to load pillar details.</Text>
      </Box>
    );
  }

  return (
    <Box className={styles.expandedPanel} padding="16">
      <Stack gap="16">
        <Box className={styles.sectionHeader}>
          <Box className={styles.sectionTitle}>
            <Text isBold>Narrative workstreams</Text>
            <Text size="small" color="subtle">
              {currentNarratives.length} attached
            </Text>
          </Box>
          <Box className={styles.typeahead}>
            <WorkstreamTypeahead
              selectedIds={excludedNarrativeTypeaheadIds}
              placeholder={`Add to ${pillarName}`}
              inputIcon={<UIIcon src={SearchLine} className={styles.searchIcon} />}
              disabled={isPending || isReordering || isAttaching}
              disableCreateWorkstream
              onSelect={(workstream) => {
                void handleAttach(workstream);
              }}
            />
          </Box>
        </Box>
        <ListHeader />
        {displayedNarratives.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToFirstScrollableAncestor]}
            onDragEnd={(event) => {
              void handleNarrativeDragEnd(event);
            }}
          >
            <SortableContext items={narrativeIds} strategy={verticalListSortingStrategy}>
              {displayedNarratives.map((dependent) => (
                <SortableNarrativeRow
                  key={dependent.id}
                  narrative={dependent}
                  dragDisabled={
                    isReordering ||
                    isAttaching ||
                    (isPending && detachPendingId !== dependent.id) ||
                    displayedNarratives.length < 2
                  }
                  removeDisabled={isPending || isReordering || isAttaching}
                  isRemoving={detachPendingId === dependent.id}
                  onRemove={(workstreamId) => {
                    void handleDetach(workstreamId);
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Text color="subtle">No narrative workstreams attached yet.</Text>
        )}
      </Stack>
    </Box>
  );
};

export const AdminPillars = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pulseUser = useAtomValue(pulseUserAtom);
  const {employee: loggedInEmployee} = useAtomValue(loggedInEmployeeAtom);
  const setSnackbar = useSetAtom(snackbarAtom);
  const sensors = useReorderSensors();
  const [expandedPillarIds, setExpandedPillarIds] = useState<number[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pillarName, setPillarName] = useState('');
  const [pendingDeletePillar, setPendingDeletePillar] = useState<WorkstreamBasic | null>(null);
  const [isReorderingPillars, setIsReorderingPillars] = useState(false);

  const timeframes = useTimeframes();
  const planningTimeframe = useMemo(
    () => parseWorkstreamTimeframe(searchParams.get('timeframe')),
    // Re-parse once the API's timeframes replace the bundled list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, timeframes]
  );

  const canFetchNarrativeRoot = Boolean(pulseUser) && canManagePillarAdmin(pulseUser);

  const {
    data: narrativeMeta,
    isLoading: isNarrativeMetaLoading,
    isError: isNarrativeMetaError,
  } = useAdminNarrativeRoot(planningTimeframe, {enabled: canFetchNarrativeRoot});
  const {
    data: pillarLevelConfigs = [],
    isLoading: isPillarLevelConfigsLoading,
    refetch: refetchPillarLevelConfigs,
  } = useAdminPillarLevelConfigs(planningTimeframe, {enabled: canFetchNarrativeRoot});
  const updatePillarLevelConfig = useUpdateAdminPillarLevelConfig(planningTimeframe);

  const narrativeRootId = narrativeMeta?.root_workstream_id;
  const {workstream: narrativeRoot, isLoading: isRootLoading} = useWorkstream(
    isNarrativeMetaLoading ? undefined : narrativeRootId
  );
  const isLoading = isNarrativeMetaLoading || Boolean(narrativeRootId && isRootLoading);
  const showNarrativeRootError = canFetchNarrativeRoot && isNarrativeMetaError;

  const handlePlanningTimeframeChange = (next: WorkstreamTimeframe) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('timeframe', next);
        return p;
      },
      {replace: true}
    );
    setExpandedPillarIds([]);
  };

  const {data: allWorkstreams = [], isLoading: isAllWorkstreamsLoading} = useTrackerOptions({
    enabled: createModalOpen,
  });
  const {createWorkstream, isPending: isCreating} = useWorkstreamCreate();
  const {editWorkstream, isPending: isUpdatingRoot} = useWorkstreamEdit();

  const {mutateAsync: createNarrativeDoc, isPending: isCreatingNarrativeDoc} = useMutation({
    mutationFn: () => getService(EditordocService).createApiV1EditordocPost('narrative'),
    onSuccess: (doc) => {
      navigate(createPlanningDocUrl({id: doc.id, name: doc.name}));
    },
    onError: () => {
      setSnackbar({text: 'Could not create narrative doc. Please try again.'});
    },
  });

  useDocumentTitle('Pillars');

  const pillars = useMemo(
    () =>
      sortDependencies(narrativeRoot?.dependencies ?? [])
        .filter(({dependent}) => !dependent.archived && isTracker(dependent))
        .map(({dependent}) => dependent),
    [narrativeRoot]
  );

  const [orderedPillars, setOrderedPillars] = useState<WorkstreamBasic[] | null>(null);

  useEffect(() => {
    setOrderedPillars(pillars);
  }, [pillars]);

  const displayedPillars = orderedPillars ?? pillars;
  const pillarLevelConfigsById = useMemo(
    () =>
      new Map<number, WorkstreamPillarLevelConfigAdminResponse>(
        pillarLevelConfigs.map((config) => [config.pillar_workstream_id, config])
      ),
    [pillarLevelConfigs]
  );

  const normalizedPillarName = pillarName.trim();
  const duplicatePillarName = displayedPillars.some(
    (pillar) => pillar.name.trim().toLowerCase() === normalizedPillarName.toLowerCase()
  );

  const existingTrackerOptions = useMemo(
    () =>
      allWorkstreams.filter(
        (workstream) =>
          !workstream.archived &&
          isTracker(workstream) &&
          narrativeRootId !== undefined &&
          workstream.id !== narrativeRootId &&
          !displayedPillars.some((pillar) => pillar.id === workstream.id)
      ),
    [allWorkstreams, displayedPillars, narrativeRootId]
  );

  if (!pulseUser) {
    return null;
  }

  if (!canManagePillarAdmin(pulseUser)) {
    navigate('/');
    return null;
  }

  const updatePillarsOnRoot = async (pillarIds: number[]) => {
    if (!narrativeRoot) {
      return;
    }

    await editWorkstream({
      data: toWorkstreamEdit(
        narrativeRoot,
        buildDependencyEdits(pillarIds, narrativeRoot.dependencies)
      ),
    });
    await refetchPillarLevelConfigs();
  };

  const handlePillarLevelConfigChange = async (
    pillarWorkstreamId: number,
    includePillarAsLevelOne: boolean
  ) => {
    try {
      await updatePillarLevelConfig.mutateAsync({
        pillarWorkstreamId,
        includePillarAsLevelOne,
      });
      setSnackbar({text: 'Pillar level setting saved.'});
    } catch {
      setSnackbar({text: 'Could not save pillar level setting.'});
    }
  };

  const handlePillarDragEnd = async ({active, over}: DragEndEvent) => {
    if (!narrativeRoot || !over || active.id === over.id || isUpdatingRoot || isReorderingPillars) {
      return;
    }

    const oldIndex = displayedPillars.findIndex(({id}) => id === active.id);
    const newIndex = displayedPillars.findIndex(({id}) => id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const previousPillars = displayedPillars;
    const nextPillars = arrayMove(displayedPillars, oldIndex, newIndex);

    setOrderedPillars(nextPillars);
    setIsReorderingPillars(true);

    try {
      await updatePillarsOnRoot(
        reorderVisibleDependencyIds({
          allDependencies: narrativeRoot.dependencies,
          visibleDependencyIds: previousPillars.map(({id}) => id),
          reorderedVisibleIds: nextPillars.map(({id}) => id),
        })
      );
    } catch {
      setOrderedPillars(previousPillars);
    } finally {
      setIsReorderingPillars(false);
    }
  };

  const attachPillarToRoot = async (pillarId: number) => {
    if (!narrativeRoot) {
      return;
    }

    await updatePillarsOnRoot([...getSortedDependencyIds(narrativeRoot.dependencies), pillarId]);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setPillarName('');
  };

  const handleAttachExistingPillar = async (pillar: WorkstreamBasic) => {
    await attachPillarToRoot(pillar.id);
    setOrderedPillars((current) =>
      (current ?? displayedPillars).some(({id}) => id === pillar.id)
        ? (current ?? displayedPillars)
        : [...(current ?? displayedPillars), pillar]
    );
    setSnackbar({text: `${pillar.name} added.`});
    setExpandedPillarIds((current) =>
      current.includes(pillar.id) ? current : [...current, pillar.id]
    );
    closeCreateModal();
  };

  const handleCreatePillar = async () => {
    if (!narrativeRoot || !normalizedPillarName) {
      return;
    }

    if (duplicatePillarName) {
      setSnackbar({text: 'A pillar with that name already exists.'});
      return;
    }

    const pillarId = await createWorkstream({
      data: {
        name: normalizedPillarName,
        description: '',
        start_date: new Date().toISOString(),
        end_date: null,
        status_cadence: 'none',
        status_due: 1,
        links: [],
        dependencies: [],
        employee_associations: [{user_id: loggedInEmployee.user_id, dri: 'dri'}],
        privacy: WorkstreamPrivacy.PUBLIC,
        private_employees: [],
        private_teams: [],
        // The pillar joins the root's timeframe.
        parent_id: narrativeRoot.id,
      },
    });

    await attachPillarToRoot(pillarId);

    setSnackbar({text: `${normalizedPillarName} created.`});
    setExpandedPillarIds((current) =>
      current.includes(pillarId) ? current : [...current, pillarId]
    );
    closeCreateModal();
  };

  return (
    <>
      <Layout.Container
        breadcrumb={[
          {children: 'Admin', to: ROUTE_PATHS.ADMIN},
          {children: 'Pillars', to: ROUTE_PATHS.ADMIN_PILLARS},
        ]}
      >
        <Stack gap="24">
          <Title
            size={24}
            withAccessoryEnd={
              <Box display="flex" alignItems="center" style={{gap: 8}}>
                <Button
                  variant="opacity"
                  size="small"
                  isLoading={isCreatingNarrativeDoc}
                  onClick={() => {
                    void createNarrativeDoc();
                  }}
                >
                  Create narrative doc
                </Button>
                <IconButton
                  aria-label="Add pillar"
                  onClick={() => setCreateModalOpen(true)}
                  variant="opacity"
                  shape="circular"
                >
                  <UIIcon src={AddLine} />
                </IconButton>
              </Box>
            }
          >
            Pillars
          </Title>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap: 12}}>
            <select
              id="admin-pillars-planning-period"
              aria-label="Planning period"
              className={atoms({
                padding: '2',
                borderRadius: 'Small',
                borderWidth: '1',
                borderStyle: 'Solid',
                borderColor: 'Border Base',
              })}
              value={planningTimeframe}
              onChange={(e) => {
                handlePlanningTimeframeChange(e.target.value as WorkstreamTimeframe);
              }}
            >
              {timeframes.map(({name}) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            {narrativeMeta ? (
              <Text size="small" color="subtle" tagName="span">
                Narrative root {narrativeMeta.root_workstream_id} · {narrativeMeta.fiscal_half} FY
                {String(narrativeMeta.fiscal_year).slice(-2).padStart(2, '0')}
              </Text>
            ) : null}
          </Box>

          <Box className={styles.panel}>
            <div className={styles.pillarHeader}>
              <Box />
              <Text size="small" color="subtle">
                Pillar
              </Text>
              <Text size="small" color="subtle">
                Narrative workstreams
              </Text>
              <Text size="small" color="subtle">
                Include pillar as level 1
              </Text>
              <Text size="small" color="subtle">
                Action
              </Text>
            </div>

            {showNarrativeRootError ? (
              <Box padding="16">
                <Text color="subtle">
                  Could not resolve the narrative root for this planning period. Check admin access
                  and try again.
                </Text>
              </Box>
            ) : isLoading ? (
              <Box display="flex" justifyContent="center" paddingY="24">
                <Spinner size="small" />
              </Box>
            ) : displayedPillars.length ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToFirstScrollableAncestor]}
                onDragEnd={(event) => {
                  void handlePillarDragEnd(event);
                }}
              >
                <SortableContext
                  items={displayedPillars.map(({id}) => id)}
                  strategy={verticalListSortingStrategy}
                >
                  {displayedPillars.map((pillar) => {
                    const isExpanded = expandedPillarIds.includes(pillar.id);
                    const levelConfig = pillarLevelConfigsById.get(pillar.id);
                    return (
                      <Box key={pillar.id}>
                        <SortablePillarRow
                          pillar={pillar}
                          isExpanded={isExpanded}
                          disabled={
                            isUpdatingRoot || isReorderingPillars || displayedPillars.length < 2
                          }
                          includePillarAsLevelOne={levelConfig?.include_pillar_as_level_one}
                          isLevelConfigLoading={isPillarLevelConfigsLoading}
                          isUpdatingLevelConfig={
                            updatePillarLevelConfig.isPending &&
                            updatePillarLevelConfig.variables?.pillarWorkstreamId === pillar.id
                          }
                          onToggle={() =>
                            setExpandedPillarIds((current) =>
                              current.includes(pillar.id)
                                ? current.filter((id) => id !== pillar.id)
                                : [...current, pillar.id]
                            )
                          }
                          onLevelConfigChange={(includePillarAsLevelOne) => {
                            void handlePillarLevelConfigChange(pillar.id, includePillarAsLevelOne);
                          }}
                          onRemove={() => {
                            setPendingDeletePillar(pillar);
                          }}
                        />

                        {isExpanded && narrativeRootId ? (
                          <ExpandedPillarPanel
                            pillarId={pillar.id}
                            pillarName={pillar.name}
                            narrativeRootId={narrativeRootId}
                          />
                        ) : null}
                      </Box>
                    );
                  })}
                </SortableContext>
              </DndContext>
            ) : (
              <Box padding="16">
                <Text color="subtle">
                  No pillars found under narrative root {narrativeRootId ?? '—'} (
                  {planningTimeframe}
                  ).
                </Text>
              </Box>
            )}
          </Box>
          <NarrativeUrlsPanel
            key={planningTimeframe}
            timeframe={planningTimeframe}
            narrativeRootId={narrativeRootId}
            enabled={canFetchNarrativeRoot}
          />
        </Stack>
      </Layout.Container>

      <Modal
        open={createModalOpen}
        withCloseButton="close"
        aria-label="Create pillar"
        onRequestClose={() => {
          if (isCreating || isUpdatingRoot) {
            return;
          }
          closeCreateModal();
        }}
      >
        <Modal.Header>
          <Text size="large" isBold>
            Add pillar
          </Text>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="12">
            <Text color="subtle">Use an existing tracker</Text>
            <WorkstreamTypeaheadComponent
              workstreamOptions={existingTrackerOptions}
              isLoading={isAllWorkstreamsLoading}
              selectedIds={displayedPillars.map((pillar) => pillar.id)}
              placeholder="Select existing tracker"
              disabled={isCreating || isUpdatingRoot || isReorderingPillars}
              disableCreateWorkstream
              includeTrackers
              onSelect={(workstream) => {
                void handleAttachExistingPillar(workstream);
              }}
            />
            {!isAllWorkstreamsLoading && !existingTrackerOptions.length ? (
              <Text size="small" color="subtle">
                No additional existing trackers are available.
              </Text>
            ) : null}
            <Box className={styles.modalDivider}>
              <Box className={styles.modalDividerLine} />
              <Text size="small" color="subtle">
                or
              </Text>
              <Box className={styles.modalDividerLine} />
            </Box>
            <Text color="subtle">Create a new pillar tracker</Text>
            <TextInput
              aria-label="Pillar name"
              placeholder="Pillar name"
              value={pillarName}
              onChange={(event) => setPillarName(event.currentTarget.value)}
            />
            {duplicatePillarName && normalizedPillarName ? (
              <Text className={atoms({color: 'Alert Base'})}>
                A pillar with that name already exists.
              </Text>
            ) : null}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="opacity"
            disabled={isCreating || isUpdatingRoot}
            onClick={() => {
              closeCreateModal();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            isLoading={isCreating || isUpdatingRoot}
            disabled={!normalizedPillarName || duplicatePillarName}
            onClick={() => {
              void handleCreatePillar();
            }}
          >
            Add pillar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={Boolean(pendingDeletePillar)}
        withCloseButton="close"
        aria-label="Remove pillar"
        onRequestClose={() => {
          if (isUpdatingRoot) {
            return;
          }
          setPendingDeletePillar(null);
        }}
      >
        <Modal.Header>
          <Text size="large" isBold>
            Remove {pendingDeletePillar?.name ?? 'pillar'} from Pillars?
          </Text>
        </Modal.Header>
        <Modal.Body>
          <Text color="subtle">
            This only removes the tracker from Pillars. The tracker itself and its attached
            workstreams will remain available.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="opacity"
            disabled={isUpdatingRoot}
            onClick={() => setPendingDeletePillar(null)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            isLoading={isUpdatingRoot}
            onClick={async () => {
              if (!pendingDeletePillar || !narrativeRoot) {
                return;
              }

              await updatePillarsOnRoot(
                getSortedDependencyIds(narrativeRoot.dependencies).filter(
                  (id) => id !== pendingDeletePillar.id
                )
              );
              setOrderedPillars((current) =>
                (current ?? displayedPillars).filter(({id}) => id !== pendingDeletePillar.id)
              );
              setExpandedPillarIds((current) =>
                current.filter((id) => id !== pendingDeletePillar.id)
              );
              setSnackbar({text: `${pendingDeletePillar.name} removed.`});
              setPendingDeletePillar(null);
            }}
          >
            Remove Pillar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
